import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIdentity } from './IdentityContext';
import { useToast } from './ToastContext';
import Spinner from './Spinner';
import { GOLD, GOLD_LT, CREAM } from '../theme';
import ImageManager from './ImageManager';
import PreviewPane from './PreviewPane';
import PublishBar from './PublishBar';

const emptyForm = { name: '', tagline: '', description: '', details: [], announceNew: false };

const inputStyle = { width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(201,162,75,.35)', color: CREAM, fontFamily: "'Cormorant Garamond', serif", fontSize: 18, padding: '10px 2px', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase', color: GOLD_LT, marginBottom: 8 };

export default function CollectionForm({ mode }) {
  const { slug: routeSlug } = useParams();
  const { authFetch } = useIdentity();
  const toast = useToast();
  const navigate = useNavigate();

  const [collection, setCollection] = useState(null); // full draft collection incl. images (edit mode only)
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(mode === 'edit');
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(() => {
    if (mode !== 'edit') {
      // The component instance can be reused across route changes (e.g.
      // navigating from editing one collection straight into "add a new
      // collection") without remounting, so `form` must be explicitly
      // reset here — otherwise the previous collection's values, including
      // a checked "announce new", silently carry over into the new one.
      setCollection(null);
      setForm(emptyForm);
      return undefined;
    }
    setLoading(true);
    return authFetch('/.netlify/functions/collections-draft')
      .then((r) => r.json())
      .then((doc) => {
        const found = (doc.collections || []).find((c) => c.slug === routeSlug);
        if (!found) {
          setNotFound(true);
          return;
        }
        setCollection(found);
        setForm({ name: found.name, tagline: found.tagline, description: found.description, details: found.details || [], announceNew: Boolean(found.announceNew) });
      })
      .finally(() => setLoading(false));
  }, [authFetch, mode, routeSlug]);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggleAnnounceNew = (e) => setForm((f) => ({ ...f, announceNew: e.target.checked }));
  const updateDetail = (i, value) => setForm((f) => ({ ...f, details: f.details.map((d, idx) => (idx === i ? value : d)) }));
  const addDetail = () => setForm((f) => ({ ...f, details: [...f.details, ''] }));
  const removeDetail = (i) => setForm((f) => ({ ...f, details: f.details.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      setSaveError('Name is required');
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      if (mode === 'new') {
        const res = await authFetch('/.netlify/functions/collections-draft', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to create collection');
        toast.success('Collection created.');
        navigate(`/admin/collections/${data.collection.slug}`);
        return;
      }
      const res = await authFetch('/.netlify/functions/collections-draft', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: routeSlug, ...form }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setCollection(data.collection);
      toast.success('Draft saved.');
    } catch (err) {
      toast.error(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={{ color: 'rgba(239,228,210,.6)' }}>Loading…</p>;
  if (notFound) return <p style={{ color: '#e08a8a' }}>Collection not found in the draft.</p>;

  const previewCollection = { slug: routeSlug || 'preview', ...form, images: collection ? collection.images : [] };

  return (
    <div>
      <Link to="/admin" style={{ display: 'inline-block', marginBottom: 16, fontSize: 13, letterSpacing: '.14em', textTransform: 'uppercase', color: GOLD_LT, textDecoration: 'none' }}>← All Collections</Link>
      <h1 style={{ fontFamily: "'Bodoni Moda', serif", fontStyle: 'italic', fontWeight: 500, fontSize: 'clamp(26px,3.2vw,36px)', margin: '0 0 24px' }}>
        {mode === 'new' ? 'New Collection' : form.name || routeSlug}
      </h1>

      <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 380px', minWidth: 0, maxWidth: 480 }}>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Name</label>
            <input style={inputStyle} value={form.name} onChange={updateField('name')} />
          </div>
          {mode === 'edit' && (
            <div style={{ marginBottom: 20, fontSize: 13, color: 'rgba(239,228,210,.45)' }}>Slug: {routeSlug} (locked)</div>
          )}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Tagline</label>
            <input style={inputStyle} value={form.tagline} onChange={updateField('tagline')} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Description</label>
            <textarea style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }} value={form.description} onChange={updateField('description')} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Details</label>
            {form.details.map((d, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input style={inputStyle} value={d} onChange={(e) => updateDetail(i, e.target.value)} />
                <button onClick={() => removeDetail(i)} style={{ background: 'none', border: 'none', color: '#e08a8a', cursor: 'pointer', fontSize: 18 }}>×</button>
              </div>
            ))}
            <button onClick={addDetail} style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: GOLD_LT, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add detail</button>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: CREAM, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.announceNew} onChange={toggleAnnounceNew} />
              Show "What's New" popup after publish
            </label>
            <p style={{ margin: '6px 0 0 26px', fontSize: 12, color: 'rgba(239,228,210,.45)' }}>Replaces whatever announcement is currently active. Only one is shown at a time.</p>
          </div>

          {saveError && <p style={{ color: '#e08a8a', fontSize: 14 }}>{saveError}</p>}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', padding: '14px 32px', background: GOLD, color: '#16110c', border: 'none', cursor: saving ? 'default' : 'pointer', opacity: saving ? .6 : 1 }}
          >
            {saving && <Spinner size={14} />}
            {saving ? 'Saving…' : 'Save Draft'}
          </button>

          {mode === 'edit' && collection && (
            <div style={{ marginTop: 40 }}>
              <ImageManager collection={collection} onChange={setCollection} />
            </div>
          )}

          {mode === 'new' && (
            <p style={{ marginTop: 24, fontSize: 13, color: 'rgba(239,228,210,.45)' }}>Save the collection first to start adding images.</p>
          )}
        </div>

        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
          <PreviewPane collection={previewCollection} />
        </div>
      </div>

      <div style={{ marginTop: 40 }}>
        <PublishBar />
      </div>
    </div>
  );
}
